import { getTokenAsync } from "../../../hooks/API/getAuthToken";
import { makeHeaders } from "../tools";
import {
  Article,
  Articles,
  ArticleWithMediaCount,
  EdsData,
  PartialArticle,
  SalesStopData,
} from "../types/article";
import { MEDIAS_SERVICE_URL, DATA_SERVICE_URL } from "./service.helper";
import { Ids } from "../types/images";

const BASE_URL = MEDIAS_SERVICE_URL + "/articles";

export const getArticleById = async (articleId: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    const response = await fetch(`${BASE_URL}/${articleId}`, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const article = (await response.json()) as Article;
    return article;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const updateArticle = async (
  articleId: string,
  article: PartialArticle
) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const url = `${BASE_URL}/${articleId}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: makeHeaders(token),
    body: JSON.stringify(article),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return;
};

export const addArticle = async (article: Partial<Article>) => {
  delete article.id;

  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(`${BASE_URL}/single`, {
    method: "POST",
    headers: makeHeaders(token),
    body: JSON.stringify(article),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const data = (await response.json()) as Ids;
  return data;
};

export const addArticlesBulk = async (
  articles: Partial<Article>[],
  groupId: string
) => {
  articles.forEach((article) => {
    delete article.id;
  });

  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(
    `${BASE_URL}${groupId ? `?groupId=${groupId}` : ""}`,
    {
      method: "POST",
      headers: makeHeaders(token),
      body: JSON.stringify(articles),
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const data = (await response.json()) as Articles;
  return data;
};

export const deleteArticleById = async (articleId: string) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(`${BASE_URL}/${articleId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return;
};

export const deleteArticleByIds = async (articleIds: string[]) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const response = await fetch(`${BASE_URL}?ids=${articleIds.join(",")}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return;
};

export const getEDSData = async (countryId: string, articleNumbers: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    const url = `${DATA_SERVICE_URL}/eds/getEdsOnArticlesByCountryId?countryId=${encodeURIComponent(
      countryId
    )}&articleNumbers=${encodeURIComponent(articleNumbers)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Response was not ok.");
    }
    const data = (await response.json()) as EdsData[];
    return data || [];
  } catch (error) {
    console.error("Fetch error:", error);
  }

  return null;
};

export const getSalesStopData = async (
  storeId: string,
  articleNumbers: string
) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    const url = `${DATA_SERVICE_URL}/sales-stops/getSalesStops?storeId=${encodeURIComponent(
      storeId
    )}&articleNumbers=${encodeURIComponent(articleNumbers)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Response was not ok.");
    }

    return (await response.json()) as SalesStopData[];
  } catch (error) {
    console.error("Fetch error:", error);
  }

  return [];
};

export const searchArticlesInMedias = async (
  storeId: string,
  searchQuery: string,
  hfb?: string
) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    let url = `${BASE_URL}/search/${encodeURIComponent(storeId)}`;
    const params: string[] = [];
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery) {
      params.push(`searchQuery=${encodeURIComponent(trimmedQuery)}`);
    }
    if (hfb) {
      params.push(`hfb=${encodeURIComponent(hfb)}`);
    }

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const articles = (await response.json()) as ArticleWithMediaCount[];
    return articles;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getArticlesByHfb = async (storeId: string, hfb: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    const url = `${BASE_URL}/hfb/${encodeURIComponent(storeId)}?hfb=${encodeURIComponent(hfb)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const articles = (await response.json()) as ArticleWithMediaCount[];
    return articles;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getCombinationArticleData = async (subArticleId: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }

    const response = await fetch(
      `${BASE_URL}/combinations?subArticleId=${subArticleId}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    return (await response.json()) as Article;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return {};
};

export async function syncCombinationArticles({
  toAdd,
  toUpdate,
  toDelete,
}: {
  toAdd: Partial<Article>[];
  toUpdate: Article[];
  toDelete: { id: string }[];
}) {
  const token = await getTokenAsync();
  if (!token) return;

  if (toAdd.length === 0 && toUpdate.length === 0 && toDelete.length === 0) {
    return; // nothing to sync
  }

  const response = await fetch(`${BASE_URL}/syncCombinationArticles`, {
    method: "POST",
    headers: makeHeaders(token),
    body: JSON.stringify({
      articlesToAdd: toAdd,
      articlesToUpdate: toUpdate,
      articlesToDelete: toDelete,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return (await response.json()) as Articles;
}
